import { FileSearchOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Select, Table, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import { formatDate, formatMoney, formatSignedDays } from "../format"
import { EvidenceDrawer } from "./EvidenceDrawer"
import { fetchMemberProjects } from "./api"
import type {
  MemberProjectsResponse,
  MemberQuery,
  ProjectFlag,
  ProjectRow
} from "./types"
import type { MemberUrlPatch } from "./use-member-url-state"

export const PROJECT_FLAGS: ProjectFlag[] = [
  "schedule_exceptions",
  "financial_exceptions",
  "on_time",
  "late_1_7",
  "late_8_14",
  "late_over_14",
  "revenue_gap",
  "cost_overrun",
  "negative_margin",
  "missing_inputs"
]

const FLAG_COLORS: Record<string, string> = {
  late: "orange",
  late_over_14: "red",
  revenue_gap: "gold",
  cost_overrun: "volcano",
  negative_margin: "magenta",
  missing_inputs: "default"
}

interface Props {
  memberId: string
  query: MemberQuery
  labels: { period?: string; type?: string; milestone?: string }
  onChange: (patch: MemberUrlPatch) => void
}

export function ProjectsView({ memberId, query, labels, onChange }: Props) {
  const { t } = useTranslation()
  const [data, setData] = useState<MemberProjectsResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<ProjectRow | null>(null)
  const key = JSON.stringify([
    query.grain,
    query.from,
    query.to,
    query.projectType,
    query.period,
    query.milestone,
    query.flag
  ])

  useEffect(() => {
    let current = true
    setFailed(false)
    setLoading(true)
    fetchMemberProjects(memberId, query)
      .then((body) => current && setData(body))
      .finally(() => current && setLoading(false))
      .catch(() => current && setFailed(true))
    return () => {
      current = false
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- key is the query serialised, so this already re-runs on every change to it
  }, [memberId, key])

  const amountsVisible = data?.amounts_visible ?? false
  const money = (value: number | null) =>
    amountsVisible ? formatMoney(value) : "—"
  const chips = [
    query.period && {
      key: "period",
      label: t("teamPerformance.member.chipPeriod", {
        value: labels.period ?? query.period
      })
    },
    query.projectType && {
      key: "projectType",
      label: t("teamPerformance.member.chipType", {
        value: labels.type ?? query.projectType
      })
    },
    query.milestone && {
      key: "milestone",
      label: t("teamPerformance.member.chipMilestone", {
        value: labels.milestone ?? query.milestone
      })
    }
  ].filter(Boolean) as { key: keyof MemberUrlPatch; label: string }[]

  return (
    <Flex vertical gap="middle">
      <Flex gap="middle" wrap align="center">
        <Select<ProjectFlag | "">
          aria-label={t("teamPerformance.member.exception")}
          className="min-w-56"
          virtual={false}
          value={query.flag ?? ""}
          onChange={(value) => onChange({ flag: value || null })}
          options={[
            { value: "", label: t("teamPerformance.member.flags.all") },
            ...PROJECT_FLAGS.filter(
              (f) => amountsVisible || f !== "negative_margin"
            ).map((f) => ({
              value: f,
              label: t(`teamPerformance.member.flags.${f}`)
            }))
          ]}
        />
        {chips.map((chip) => (
          <Tag
            key={chip.key}
            closable
            onClose={() => onChange({ [chip.key]: null })}
          >
            {chip.label}
          </Tag>
        ))}
      </Flex>
      {failed && (
        <Alert
          type="error"
          showIcon
          message={t("teamPerformance.states.loadFailed")}
        />
      )}
      <Table<ProjectRow>
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={data?.projects ?? []}
        scroll={{ x: 1200 }}
        pagination={
          (data?.projects.length ?? 0) > 20
            ? { pageSize: 20, showSizeChanger: false }
            : false
        }
        locale={{ emptyText: t("teamPerformance.member.noProjects") }}
        columns={[
          {
            key: "name",
            title: t("teamPerformance.member.project"),
            fixed: "left",
            width: 220,
            render: (_, row) => (
              <Flex gap={4} align="center">
                <Link to={RoutesPaths.project.view(row.id).overview}>
                  {row.name}
                </Link>
                <Button
                  size="small"
                  type="text"
                  icon={<FileSearchOutlined />}
                  aria-label={t("teamPerformance.member.evidenceFor", {
                    name: row.name
                  })}
                  onClick={() => setSelected(row)}
                />
              </Flex>
            )
          },
          {
            key: "type",
            title: t("teamPerformance.evidence.type"),
            render: (_, row) =>
              row.project_type ?? t("teamPerformance.filters.unknownType")
          },
          {
            key: "closed",
            title: t("teamPerformance.member.closed"),
            render: (_, row) => formatDate(row.closed_date)
          },
          {
            key: "planned",
            title: t("teamPerformance.member.plannedEnd"),
            render: (_, row) => formatDate(row.planned_end)
          },
          {
            key: "actual",
            title: t("teamPerformance.member.actualEnd"),
            render: (_, row) => formatDate(row.actual_end)
          },
          {
            key: "variance",
            title: t("teamPerformance.evidence.variance"),
            align: "right",
            render: (_, row) => formatSignedDays(row.variance_days)
          },
          ...(query.milestone
            ? [
                {
                  key: "milestone",
                  title:
                    labels.milestone ??
                    t("teamPerformance.milestones.milestone"),
                  align: "right" as const,
                  render: (_: unknown, row: ProjectRow) =>
                    row.milestone_focus
                      ? formatSignedDays(row.milestone_focus.variance_days)
                      : "—"
                }
              ]
            : []),
          {
            key: "expected",
            title: t("teamPerformance.member.expected"),
            align: "right",
            render: (_, row) => money(row.expected_income)
          },
          {
            key: "billed",
            title: t("teamPerformance.evidence.billed"),
            align: "right",
            render: (_, row) => money(row.billed)
          },
          {
            key: "budget",
            title: t("teamPerformance.evidence.budget"),
            align: "right",
            render: (_, row) => money(row.budget)
          },
          {
            key: "cost",
            title: t("teamPerformance.evidence.cost"),
            align: "right",
            render: (_, row) => money(row.cost)
          },
          {
            key: "flags",
            title: t("teamPerformance.member.flagsTitle"),
            width: 240,
            render: (_, row) => (
              <Flex gap={4} wrap>
                {row.flags
                  .filter((f) => f !== "late_over_7")
                  .map((f) => (
                    <Tag key={f} color={FLAG_COLORS[f]} bordered={false}>
                      {t(`teamPerformance.member.rowFlags.${f}`)}
                    </Tag>
                  ))}
              </Flex>
            )
          }
        ]}
      />
      <Typography.Text type="secondary" className="text-xs">
        {t("teamPerformance.member.ledgerNote")}
      </Typography.Text>
      <EvidenceDrawer
        row={selected}
        amountsVisible={amountsVisible}
        onClose={() => setSelected(null)}
      />
    </Flex>
  )
}
